import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * SyncedHScroll — table-local horizontal navigation.
 * ONE persistent scrollbar, sticky at the viewport bottom while the table is
 * in view, resting at the table's bottom edge otherwise. The native scroller
 * bar is hidden so there is never a duplicated bar, and edge fades signal
 * that more columns exist left/right. Presentation-only.
 */
export function SyncedHScroll({ children, className = '', testId }) {
  const barRef = useRef(null);
  const bodyRef = useRef(null);
  const [dims, setDims] = useState({ scrollW: 0, clientW: 0 });
  const [edges, setEdges] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    setDims({ scrollW: el.scrollWidth, clientW: el.clientWidth });
    setEdges({
      left: el.scrollLeft > 1,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
  }, []);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    const table = el.querySelector('table');
    if (table) ro.observe(table);
    measure();
    return () => ro.disconnect();
  }, [measure]);

  const sync = (src, dst) => {
    if (src.current && dst.current && dst.current.scrollLeft !== src.current.scrollLeft) {
      dst.current.scrollLeft = src.current.scrollLeft;
    }
  };
  const needsScroll = dims.scrollW > dims.clientW + 1;

  return (
    <div className={className}>
      <div className="relative">
        <div
          ref={bodyRef}
          onScroll={() => { sync(bodyRef, barRef); measure(); }}
          className="overflow-x-auto cth-hide-scrollbar"
          data-testid={testId}
        >
          {children}
        </div>
        {needsScroll && edges.left && (
          <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-8 z-10 bg-gradient-to-r from-background/90 to-transparent" />
        )}
        {needsScroll && edges.right && (
          <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-8 z-10 bg-gradient-to-l from-background/90 to-transparent" />
        )}
      </div>
      <div
        ref={barRef}
        onScroll={() => { sync(barRef, bodyRef); measure(); }}
        aria-hidden="true"
        data-testid={testId ? `${testId}-bar` : undefined}
        className={`cth-hscrollbar sticky bottom-0 z-20 overflow-x-scroll overflow-y-hidden border-t border-border bg-background rounded-b-lg py-1 ${needsScroll ? '' : 'hidden'}`}
      >
        <div style={{ width: dims.scrollW || '100%', height: 1 }} />
      </div>
    </div>
  );
}
